import * as Yup from 'yup';

const siteContactSchema = Yup.object().shape({
    email: Yup.string().email('Invalid email').required('Email is required'),
    lastName: Yup.string().required('Last name is required'),
    position: Yup.string().required('Position is required'),
    firstName: Yup.string().required('First name is required'),
    mobileNumber: Yup.string().required('Mobile number is required'),
    telephoneNumber: Yup.string().required('Telephone number is required'),
});

const outletSchema = (postalLabel: string) => {
    return Yup.object().shape({
        name: Yup.string().required('Outlet name is required'),
        siteContact: siteContactSchema,
        bankingDetails: Yup.object().shape({
            accountName: Yup.string().when(['serviceChargeCalculatedAt'], {
                is: (serviceChargeCalculatedAt) => {
                    return serviceChargeCalculatedAt === 'OUTLETLEVEL';
                },
                then: Yup.string().required('Account name is required'),
            }),
            bankIdentifierCode: Yup.string().when(['serviceChargeCalculatedAt'], {
                is: (serviceChargeCalculatedAt) => {
                    return serviceChargeCalculatedAt === 'OUTLETLEVEL';
                },
                then: Yup.string().required('Bank identifier code is required'),
            }),
            internationalBankAccountNumber: Yup.string().when(['serviceChargeCalculatedAt'], {
                is: (serviceChargeCalculatedAt) => {
                    return serviceChargeCalculatedAt === 'OUTLETLEVEL';
                },
                then: Yup.string().required('International bank account number is required'),
            }),
        }),
        tradingAddress: Yup.object().shape({
            county: Yup.string().required('County is required'),
            country: Yup.string().required('Country is required'),
            eircode: Yup.string().required(`${postalLabel} is required`),
            townOrCity: Yup.string().required('Town or city is required'),
            addressLine1: Yup.string().required('Address line 1 is required'),
            addressLine2: Yup.string(),
        }),
    });
};

export const outletInformationValidations = (postalLabel: string) => {
    return Yup.object().shape({
        outlets: Yup.array().of(outletSchema(postalLabel)), // Corrected to use Yup.array().of()
        principals: Yup.array().of(Yup.object()), // Add validation for principals if needed
        aboutBusiness: Yup.object().shape({
            vat: Yup.object().shape({
                vatNumber: Yup.string(),
                vatVerified: Yup.boolean(),
                vatRegistered: Yup.boolean(),
            }),
            website: Yup.string().url('Invalid URL'),
            isCharity: Yup.boolean(),
            tradingName: Yup.string(),
            businessType: Yup.string(),
            charityNumber: Yup.string(),
            legalCompanyName: Yup.string(),
            registeredNumber: Yup.string(),
            registeredAddress: Yup.object().shape({
                county: Yup.string(),
                country: Yup.string(),
                eircode: Yup.string(),
                townOrCity: Yup.string(),
                addressLine1: Yup.string(),
                addressLine2: Yup.string(),
            }),
        }),
        isStepCompleted: Yup.boolean(),
        numberOfOutlets: Yup.number().integer().min(0),
        sameAsRegisteredAddress: Yup.boolean(),
    });
};

export const addServiceRequirementsValidation = (postalLabel: string) => {
    return Yup.object().shape({
        service: Yup.object().shape({
            serviceType: Yup.string().required('Select Service Type'),
            contractTerm: Yup.string().required('Select Contract Term'),
            selectedOnlineOffer: Yup.string()
                .when(['serviceType'], {
                    is: (serviceType) => {
                        return serviceType === 'ONLINE';
                    },
                    then: Yup.string().required('Select Online Pricing Offer'),
                })
                .nullable(),
            selectedTerminalOffer: Yup.string()
                .when(['serviceType'], {
                    is: (serviceType) => {
                        return serviceType === 'TERMINAL';
                    },
                    then: Yup.string().required('Select Terminal Pricing Offer'),
                })
                .nullable(),
            shouldApplyPromo: Yup.string(),
            promoOffer: Yup.string().when('shouldApplyPromo', {
                is: (val) => {
                    return val === 'yes';
                },
                then: Yup.string().required('Select a promo offer'),
                otherwise: Yup.string(),
            }),
        }),
        order: Yup.object()
            .shape({
                primary: Yup.array().of(
                    Yup.object().shape({
                        id: Yup.number().required('Product ID is required'),
                        name: Yup.string().required('Product Name is required'),
                        imageUrl: Yup.string().required('Product Image URL is required'),
                        __typename: Yup.string().required('Product Type is required'),
                    })
                ),
                secondary: Yup.array().of(
                    Yup.object().shape({
                        id: Yup.number().required('Product ID is required'),
                        name: Yup.string().required('Product Name is required'),
                        imageUrl: Yup.string().required('Product Image URL is required'),
                        __typename: Yup.string().required('Product Type is required'),
                    })
                ),
            })
            .test(
                'at-least-one-item',
                'At least one item is required in either primary or secondary',
                (value) => {
                    const { primary, secondary } = value || {};
                    return (primary && primary.length > 0) || (secondary && secondary.length > 0);
                }
            ),
        existingOutlet: Yup.string(),
        outlets: Yup.array().when('existingOutlet', {
            is: 'No',
            then: Yup.array()
                .of(
                    Yup.object().shape({
                        name: Yup.string().required('Outlet Name is required'),
                        siteContact: Yup.object().shape({
                            email: Yup.string()
                                .email('Invalid email format')
                                .required('Email is required'),
                            firstName: Yup.string().required('First Name is required'),
                            lastName: Yup.string().required('Last Name is required'),
                            position: Yup.string().required('Position is required'),
                            mobileNumber: Yup.string().required('Mobile Number is required'),
                            telephoneNumber: Yup.string(),
                        }),
                        tradingAddress: Yup.object().shape({
                            county: Yup.string().required('County is required'),
                            country: Yup.string().required('Country is required'),
                            eircode: Yup.string().required(`${postalLabel} is required`),
                            addressLine1: Yup.string().required('Address Line 1 is required'),
                            townOrCity: Yup.string().required('Town or City is required'),

                            addressLine2: Yup.string().required('Address Line 2 is required'),
                        }),
                    })
                )
                .min(1, 'At least one outlet is required when existingOutlet is No')
                .required('Outlets are required when existingOutlet is No'),
            otherwise: Yup.array().when('existingOutlet', {
                is: 'Yes',
                then: Yup.array().of(
                    Yup.object().shape({
                        name: Yup.string().required('Select an outlet'),
                    })
                ),
                otherwise: Yup.array().notRequired(),
            }),
        }),
    });
};
